/* eslint-disable no-unused-expressions, consistent-return, no-undef,
@typescript-eslint/no-unused-expressions, no-return-assign, no-param-reassign */
import L from 'leaflet';

import { LineInterface } from 'store/ducks/lines/types';

// Interfaces
import { ILineGeoJson, ILineGeoJsonProps } from 'common/lineRoute.interface';

// Constants
import { CONST_LINES_OBJECT } from 'constants/lines';

// components
import * as MarkerIcons from 'components/Leaflet/MarkerIcons';

export interface PointInterface {
  latlng: { lat: number; lng: number };
}

/**
 * Centers the map from the coordinates of markers and polylines.
 */
export function centerMap(mapRef: L.Map) {
  const bounds = L.latLngBounds([]);
  mapRef.eachLayer((layer) => {
    if (layer instanceof L.Marker) bounds.extend(layer.getLatLng());
    if (layer instanceof L.Polyline) bounds.extend(layer.getBounds());
  });
  if (bounds && bounds.isValid()) mapRef.fitBounds(bounds);
}

/**
 * Check point and open drawer
 */
function checkPointAndOpenDrawer(
  selectedLines: LineInterface[],
  point: PointInterface,
  toggleInfoDrawerPoint: (open: boolean, id: any, stopData: any) => void,
) {
  const currentPoint: any[] = [];
  selectedLines.forEach(({ LineStops }: any) => {
    if (Array.isArray(LineStops)) {
      const aux = LineStops.find((item: any) => {
        if (
          String(item.Stop.startLat) === String(point.latlng.lat) &&
          String(item.Stop.startLong) === String(point.latlng.lng)
        ) {
          return item;
        }
      });
      currentPoint.push(aux);
    }
  });

  const item = currentPoint.find((item: any) => item);
  const stop = currentPoint.find((item: any) => item)?.Stop;

  if (toggleInfoDrawerPoint instanceof Function && item?.id && stop) {
    toggleInfoDrawerPoint(true, item.id, stop);
  }
}

/**
 * Check if element exists in DOM
 */
function isVisible(elem: Element | null) {
  if (elem) return window.getComputedStyle(elem).display !== 'none';
}

/**
 * Hide or Show Elements on Map
 */
function hideOrShowElements(path: string, display: string, multiple?: boolean) {
  if (multiple) {
    const images = document.querySelectorAll(path) as NodeListOf<
      HTMLImageElement
    > | null;
    if (images) {
      images.forEach((image) => {
        if (image?.currentSrc?.startsWith('data:')) return;
        return (image.style.display = display);
      });
    }
  } else {
    const image = document.querySelector(path) as HTMLImageElement | null;
    if (image) {
      if (image?.currentSrc?.startsWith('data:')) return;
      return (image.style.display = display);
    }
  }
}

/**
 * Toggle markers from the map.
 */
export function toggleMarkersFromMap() {
  const markerPath =
    '.leaflet-marker-icon.leaflet-zoom-animated.leaflet-interactive';
  const shadowPath = '.leaflet-pane.leaflet-shadow-pane';

  const marker = document.querySelector(markerPath);
  const shadow = document.querySelector(shadowPath);

  if (isVisible(marker) || isVisible(shadow)) {
    hideOrShowElements(markerPath, 'none', true);
    hideOrShowElements(shadowPath, 'none');
  } else {
    hideOrShowElements(markerPath, 'block', true);
    hideOrShowElements(shadowPath, 'block');
  }
}

/**
 * Toggle line from the Map.
 */
export function toggleLinesFromMap() {
  const overlayPath = '.leaflet-pane.leaflet-overlay-pane';
  const overlay = document.querySelector(overlayPath);

  if (isVisible(overlay)) {
    hideOrShowElements(overlayPath, 'none');
  } else {
    hideOrShowElements(overlayPath, 'block');
  }
}

/**
 * Remove all markers and polylines from the map.
 */
export function clearMap(mapRef: L.Map) {
  mapRef.eachLayer((layer) => {
    if (layer instanceof L.Marker || layer instanceof L.Polyline) {
      mapRef.removeLayer(layer);
    }
  });
}

/**
 * Creates the line geojson layer.
 */
export function createLineGeoJsonLayer({
  geojson,
  pathStyle,
  markerIcon,
  selectedLines,
  showStops,
  toggleInfoDrawerPoint,
  onPath,
  onEachRoutingPoint,
  onEachStoppingPoint,
}: {
  geojson: ILineGeoJson;
  pathStyle?: L.PathOptions;
  markerIcon?: { stoppingPoint?: L.Icon; routingPoint?: L.Icon };
  selectedLines?: any[];
  showStops?: boolean | undefined;
  toggleInfoDrawerPoint?: (open: boolean, id: any, stopData: any) => void;
  onPath?: (props: ILineGeoJsonProps, polyline: L.Polyline) => void;
  onEachRoutingPoint?: (props: ILineGeoJsonProps, marker: L.Marker) => void;
  onEachStoppingPoint?: (props: ILineGeoJsonProps, marker: L.Marker) => void;
}) {
  return L.geoJSON<ILineGeoJsonProps>(geojson, {
    onEachFeature: ({ properties }, layer) => {
      layer.on('click', (point: PointInterface) => {
        if (selectedLines && (showStops || showStops === undefined)) {
          if (toggleInfoDrawerPoint instanceof Function) {
            checkPointAndOpenDrawer(
              selectedLines,
              point,
              toggleInfoDrawerPoint,
            );
          }
        }
      });

      if (layer instanceof L.Marker) {
        const t = properties && properties.type;
        if (t === CONST_LINES_OBJECT.GEOJSON_PROPS.TYPES.STOPPING_POINT.KEY) {
          var TRANSPARENT_ICON = L.divIcon({ className: 'usulessClass' });
          if (markerIcon && markerIcon.stoppingPoint) {
            if (showStops || showStops === undefined) {
              layer.setIcon(markerIcon.stoppingPoint);
            } else {
              layer.setIcon(TRANSPARENT_ICON);
            }
          } else {
            if (showStops || showStops === undefined) {
              layer.setIcon(MarkerIcons.blueIcon);
            } else {
              layer.setIcon(TRANSPARENT_ICON);
            }
          }
          if (onEachStoppingPoint) onEachStoppingPoint(properties, layer);
        }
        if (t === CONST_LINES_OBJECT.GEOJSON_PROPS.TYPES.ROUTING_POINT.KEY) {
          if (markerIcon && markerIcon.routingPoint) {
            layer.setIcon(markerIcon.routingPoint);
          } else {
            layer.setIcon(MarkerIcons.greyIcon);
          }
          if (onEachRoutingPoint) onEachRoutingPoint(properties, layer);
        }
      }
      if (layer instanceof L.Polyline) {
        if (pathStyle) {
          layer.setStyle(pathStyle);
        }
        if (onPath) onPath(properties, layer);
      }
    },
  });
}

/**
 * Loads the geojson of a line on the map.
 */
export function loadGeoJsonLine({
  mapRef,
  geojson,
  pathStyle,
  markerIcon,
  selectedLines,
  showStops,
  toggleInfoDrawerPoint,
  onPath,
  onEachRoutingPoint,
  onEachStoppingPoint,
}: {
  mapRef: L.Map;
  geojson: ILineGeoJson;
  pathStyle?: L.PathOptions;
  markerIcon?: { stoppingPoint?: L.Icon; routingPoint?: L.Icon };
  selectedLines?: any[];
  showStops?: boolean | undefined;
  toggleInfoDrawerPoint?: (open: boolean, id: any, stopData: any) => void;
  onPath?: (props: ILineGeoJsonProps, polyline: L.Polyline) => void;
  onEachRoutingPoint?: (props: ILineGeoJsonProps, marker: L.Marker) => void;
  onEachStoppingPoint?: (props: ILineGeoJsonProps, marker: L.Marker) => void;
}) {
  createLineGeoJsonLayer({
    geojson,
    pathStyle,
    markerIcon,
    selectedLines,
    showStops,
    toggleInfoDrawerPoint,
    onPath,
    onEachRoutingPoint,
    onEachStoppingPoint,
  })?.addTo(mapRef);
}
